import { IonSpinner } from '@ionic/react';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';
import { PaymentRequest } from '@stripe/stripe-js';
import React from 'react';

import { STRIPE_BUSINESS_ACCOUNT_COUNTRY } from '@/constants';
import { usePollingUser } from '@/hooks/usePollingUser';
import { amplitudeModule } from '@/modules/amplitude';
import { fireConfetti } from '@/modules/confetti';
import { EV_EM_SUBSCRIPTION_KEY, eventEmitter } from '@/modules/event-emitter';
import { FacebookModule } from '@/modules/facebook';
import { StripeV2CoinsPrice } from '@/services/stripe/dto';
import { canLogEvents } from '@/utils/canLogEvents';

import {
  MessageStyles,
  PaymentSystemButtonStyles,
} from './CoinsPaymentSystemButton.styles';

export const CoinsPaymentSystemButton: React.FC<{
  product: StripeV2CoinsPrice;
  disabled: boolean;
  clientSecret: string;
  onCoinsIncrease?: () => void;
}> = ({ product, disabled, clientSecret, onCoinsIncrease }) => {
  const { startPolling, stopPolling } = usePollingUser();

  const [stripePurchase, setStripePurchase] = React.useState({
    error: '',
    success: false,
    isSubmitting: false,
  });

  const stripe = useStripe();

  const [paymentRequest, setPaymentRequest] =
    React.useState<PaymentRequest | null>(null);
  const [isPaymentRequestAvailable, setIsPaymentRequestAvailable] =
    React.useState(false);

  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    if (!stripe) return;

    // Create a PaymentRequest for Apple Pay (coins purchase)
    const pr = stripe.paymentRequest({
      country: STRIPE_BUSINESS_ACCOUNT_COUNTRY,
      currency: 'usd',
      disableWallets: ['googlePay', 'browserCard', 'link'],
      total: {
        label: product.label,
        amount: Math.round(product.price * 100),
      },
      requestPayerName: false,
      requestPayerEmail: false,
      // No recurringPaymentRequest for coins purchase
    });

    // Check if the Payment Request button is available
    pr.canMakePayment().then((result) => {
      if (result) {
        setPaymentRequest(pr);
        pr.on('source', () => {});
        setIsPaymentRequestAvailable(true);
      }
    });

    // Listen for the `paymentmethod` event (when the user completes payment)
    pr.on('paymentmethod', async (event) => {
      setStripePurchase((prev) => ({ ...prev, isSubmitting: true }));
      try {
        const { error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: event.paymentMethod.id,
        });

        if (error) {
          setStripePurchase({
            error: `Payment failed: ${error.message}`,
            success: false,
            isSubmitting: false,
          });
          event.complete('fail');
        } else {
          setStripePurchase({
            error: '',
            success: true,
            isSubmitting: false,
          });
          startPolling();
          if (canLogEvents()) {
            amplitudeModule.logEvent('web_purchase', {
              plan: product.id,
            });
            FacebookModule.trackEvent('Purchase', {
              value: product.price,
              currency: 'USD',
            });

            // HilltopAds postback
            window.sendHilltopAdsPostback && window.sendHilltopAdsPostback();

            // TrafficStars postback
            window.sendTrafficStarsPostback &&
              window.sendTrafficStarsPostback();
          }
          event.complete('success');
        }
      } catch (err) {
        let message = '';
        if (err instanceof Error) {
          message = `Unexpected error: ${err.message}`;
        } else {
          message = 'Unexpected error occurred.';
        }
        setStripePurchase({
          error: message,
          success: false,
          isSubmitting: false,
        });
        event.complete('fail');
      }
    });

    // Handle unexpected errors or cancellations
    pr.on('cancel', () => {
      setMessage('Payment cancelled by the user.');
    });

    return () => {
      pr.off('paymentmethod');
      pr.off('cancel');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  React.useEffect(() => {
    const subscription = eventEmitter.subscribe(
      EV_EM_SUBSCRIPTION_KEY.COINS_BALANCE_INCREASE,
      () => {
        stopPolling();
        setStripePurchase((prev) => ({ ...prev, isSubmitting: false }));
        fireConfetti();
        onCoinsIncrease?.();
      },
    );
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PaymentSystemButtonStyles disabled={disabled}>
      {isPaymentRequestAvailable && paymentRequest && (
        <PaymentRequestButtonElement
          options={{
            paymentRequest,
          }}
        />
      )}
      {message && <MessageStyles color="danger">{message}</MessageStyles>}
      {stripePurchase.error && (
        <MessageStyles color="danger">{stripePurchase.error}</MessageStyles>
      )}
      {stripePurchase.success && (
        <MessageStyles>
          We are processing your payment...
          <IonSpinner name="circles" />
        </MessageStyles>
      )}
    </PaymentSystemButtonStyles>
  );
};
